import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

import { FormField, FormSelect } from '../../components/FormField'
import { RecordPicker } from '../../components/RecordPicker'
import { FilterCell, FilterSummary, SortableHeader } from '../../components/TableHeads'
import { describeBookRecord, type BookRecord } from '../../components/bookRecord'
import { TableScroll } from '../../components/TableScroll'
import { historyRow } from '../../components/historyRow'
import { apiErrorMessage } from '../../lib/errors'
import { formatDate, formatNumber } from '../../lib/format'
import { keepingView } from '../../lib/tableAddress'
import type { TableColumn } from '../../lib/tableView'
import { useAddressTableView } from '../../lib/useAddressTableView'
import { filterChoice, useFilterRecord } from '../../lib/useFilterRecord'
import { EQUIPMENT_TYPES, getCarrier, searchCarriers, type EquipmentType } from '../carriers/api'
import {
  BOARD_SORTS,
  BOARD_STATUSES,
  listAvailability,
  type AvailabilityFilter,
  type BoardSort,
  type BoardStatus,
  type TruckAvailability,
} from './api'
import { boardFilterFrom, boardSearchFrom, isNarrowed } from './boardFilter'
import { StandingBadge } from './StandingBadge'
import { unitEquipment, unitName } from './unitName'

/** A carrier's name, asked of the carrier itself: an address holds only its id. */
const carrierName = (id: string) => getCarrier(id).then((carrier) => carrier.name)

/**
 * What the board's columns sort and filter by (TRKC-110). The carrier, the equipment, the
 * unit's state, the days and the situation are filtered from the bar above — the server's
 * filters — so their headings sort and their boxes stay empty; the unit, its room and its
 * city are filtered here. Words sort as the screen's language says them.
 */
function boardColumns(t: TFunction): TableColumn<TruckAvailability>[] {
  const unit = (posting: TruckAvailability) => `${unitName(posting, t)} ${posting.reference} ${posting.carrierName}`
  const place = (posting: TruckAvailability) => `${posting.city}, ${posting.state}`

  return [
    { key: 'unit', sortValue: (posting) => unitName(posting, t), filter: { kind: 'text', text: unit } },
    { key: 'equipment', sortValue: (posting) => unitEquipment(posting, t) },
    {
      key: 'room',
      sortValue: (posting) => posting.availableWeightPounds,
      filter: { kind: 'atLeast', value: (posting) => posting.availableWeightPounds, keepUnknown: false },
    },
    { key: 'window', sortValue: (posting) => posting.availableFrom },
    { key: 'location', sortValue: place, filter: { kind: 'text', text: place } },
    { key: 'standing', sortValue: (posting) => STANDING_RANK[posting.standing] },
  ]
}

/**
 * The board's groups: open, then expired, then withdrawn. A sorted column sorts within them,
 * as the board's own sort does — a greyed row never sits among the ones on offer (TRKC-90).
 */
const STANDING_RANK: Record<TruckAvailability['standing'], number> = { Open: 0, Expired: 1, Withdrawn: 2 }
const standingRank = (posting: TruckAvailability) => STANDING_RANK[posting.standing]

/** The heading each column's filter box is named after. */
const BOARD_HEADINGS: Record<string, string> = {
  unit: 'availability.unit',
  equipment: 'availability.equipment',
  room: 'availability.room',
  window: 'availability.window',
  location: 'availability.location',
  standing: 'availability.standing',
}

/**
 * The posting board: every unit on offer, soonest available first, then the ones whose
 * last day has passed, greyed. Withdrawn postings are history and only shown when asked
 * for. It narrows by carrier, equipment, the unit's state, the days it is free and its
 * situation, and reads by soonest available, carrier or state (TRKC-90) — always within
 * those groups. The server decides the order, the standing and what a filter matches;
 * this page only lays them out.
 */
export function AvailabilityPage() {
  const { t, i18n } = useTranslation()

  // What the board is narrowed to lives in its address (Lino, 2026-09-27): going back to it
  // from a posting, or opening a shared link, shows the same board. The controls answer from
  // state the page owns, with the address as its copy: the router applies an address change
  // inside a transition, and controls fed straight from it showed their old value for a
  // moment — long enough for a ticked box to read as never ticked (the E2E caught it), and
  // for a date being typed to be set back under the keyboard.
  const [searchParams, setSearchParams] = useSearchParams()
  const address = searchParams.toString()
  const [filter, setFilter] = useState(() => boardFilterFrom(searchParams))

  const [postings, setPostings] = useState<TruckAvailability[] | null>(null)
  // The carrier chosen in the filter's picker, with its name — the address holds only the id.
  const [chosenCarrier, setChosenCarrier] = useState<BookRecord | null>(null)
  // What the state box holds, apart from the filter it feeds: the board narrows by a whole
  // state or not at all — one letter is half a state (the load book's rule, Copilot on #32).
  const [stateText, setStateText] = useState(filter.state ?? '')
  // Stored unresolved and translated at render, so a language switch re-translates it.
  const [loadFailure, setLoadFailure] = useState<{ error: unknown } | null>(null)

  // The address can also change under the page — the menu's plain link to the board, the
  // browser's Back — and the board follows it. Following its own writes changes nothing:
  // the same filter comes back, and the request below is keyed on what the filter says.
  useEffect(() => {
    const next = boardFilterFrom(new URLSearchParams(address))
    setFilter(next)
    setStateText((typed) => (next.state === undefined && typed.length < 2 ? typed : (next.state ?? '')))
  }, [address])

  const narrow = (changes: Partial<AvailabilityFilter>) => {
    const next = { ...filter, ...changes }
    setFilter(next)
    // The column view the address holds goes along (TRKC-110).
    setSearchParams((current) => keepingView(boardSearchFrom(next), current), { replace: true })
  }

  const asked = boardSearchFrom(filter).toString()

  // A carrier id in the address can be shaped right and still name nobody — a link from
  // elsewhere, say — and sent, it would narrow the board to nothing under a picker reading
  // "Any carrier" (Copilot on #36). Unless it is the one just chosen in the picker, it is
  // asked of the carrier itself (GPT-5.6 Sol on #36): until that is known the board asks for
  // nothing; an id nobody has is dropped before anything is asked; one that cannot be
  // checked is kept, and shown as chosen.
  const carrierRecord = useFilterRecord(filter.carrierId, chosenCarrier, carrierName)
  const holdForCarrier = carrierRecord.state === 'checking' || carrierRecord.state === 'gone'

  useEffect(() => {
    if (carrierRecord.state === 'gone') {
      const next = { ...filter, carrierId: undefined }
      setFilter(next)
      setSearchParams((current) => keepingView(boardSearchFrom(next), current), { replace: true })
    }
  }, [carrierRecord.state, filter, setSearchParams])

  // Every change of filter asks the server again, and answers can land out of order: only
  // the latest request may write, so a slower answer to a choice the screen has left — or
  // its failure — never replaces the board the controls describe (GPT-5.6 Sol on #32).
  useEffect(() => {
    if (holdForCarrier) {
      return
    }

    let active = true
    setLoadFailure(null)

    listAvailability(boardFilterFrom(new URLSearchParams(asked)))
      .then((found) => {
        if (active) {
          setPostings(found)
        }
      })
      .catch((caught: unknown) => {
        if (active) {
          setPostings(null)
          setLoadFailure({ error: caught })
        }
      })

    return () => {
      active = false
    }
  }, [asked, holdForCarrier])

  const columns = useMemo(() => boardColumns(t), [t])
  const view = useAddressTableView(postings ?? NO_POSTINGS, columns, standingRank)

  // A range that ends before it starts holds no day, and the board says so instead of
  // leaving an empty list to explain itself.
  const inverted =
    filter.availableFrom !== undefined && filter.availableTo !== undefined && filter.availableFrom > filter.availableTo

  return (
    <section className="page">
      <header className="page__header page__header--row">
        <div>
          <h1>{t('availability.title')}</h1>
          <p className="page__subtitle">{t('availability.subtitle')}</p>
        </div>
        <Link className="button" to="/availability/new">
          {t('availability.post')}
        </Link>
      </header>

      <div className="toolbar toolbar--filters">
        {/* The carrier kept in the address is shown as chosen — by name when the carrier
            answered — so the board never looks whole while it is narrowed. */}
        <RecordPicker<BookRecord>
          name="carrierFilter"
          label={t('availability.filters.carrier')}
          value={filterChoice(filter.carrierId, carrierRecord, t('availability.filters.linkedCarrier'))}
          onChange={(record) => {
            setChosenCarrier(record)
            narrow({ carrierId: record?.id })
          }}
          search={searchCarriers}
          describe={(record) => describeBookRecord(record, t)}
          noneLabel={t('availability.filters.anyCarrier')}
          placeholder={t('availability.filters.anyCarrier')}
        />
        <FormSelect
          name="equipmentFilter"
          label={t('availability.filters.equipment')}
          value={filter.equipment ?? ''}
          onChange={(value) => narrow({ equipment: value === '' ? undefined : (value as EquipmentType) })}
          options={[
            { value: '', label: t('availability.filters.anyEquipment') },
            ...EQUIPMENT_TYPES.map((type) => ({ value: type, label: t(`carriers.types.${type}`) })),
          ]}
        />
        <FormField
          name="stateFilter"
          label={t('availability.filters.state')}
          value={stateText}
          onChange={(value) => {
            const state = value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2)
            const whole = state.length === 2 ? state : undefined
            setStateText(state)
            if (whole !== filter.state) {
              narrow({ state: whole })
            }
          }}
          maxLength={2}
          placeholder="TX"
          width="narrow"
        />
        <FormField
          name="availableFromFilter"
          type="date"
          label={t('availability.filters.availableFrom')}
          value={filter.availableFrom ?? ''}
          onChange={(value) => narrow({ availableFrom: value === '' ? undefined : value })}
        />
        <FormField
          name="availableToFilter"
          type="date"
          label={t('availability.filters.availableTo')}
          value={filter.availableTo ?? ''}
          onChange={(value) => narrow({ availableTo: value === '' ? undefined : value })}
        />
        <FormSelect
          name="statusFilter"
          label={t('availability.filters.status')}
          value={filter.status ?? ''}
          onChange={(value) => narrow({ status: value === '' ? undefined : (value as BoardStatus) })}
          options={[
            { value: '', label: t('availability.filters.statuses.Current') },
            ...BOARD_STATUSES.map((status) => ({ value: status, label: t(`availability.filters.statuses.${status}`) })),
          ]}
        />
        <FormSelect
          name="sortFilter"
          label={t('availability.filters.sort')}
          value={filter.sort ?? 'Available'}
          onChange={(value) => narrow({ sort: value as BoardSort })}
          options={BOARD_SORTS.map((sort) => ({ value: sort, label: t(`availability.filters.sorts.${sort}`) }))}
        />
      </div>

      {loadFailure !== null && (
        <p className="alert" role="alert">
          {apiErrorMessage(loadFailure.error, t, 'availability.loadError')}
        </p>
      )}

      {postings !== null && postings.length === 0 && (
        <p className="placeholder">
          {inverted
            ? t('availability.filters.inverted')
            : isNarrowed(filter)
              ? t('availability.filters.noneMatch')
              : t('availability.empty')}
        </p>
      )}

      {postings !== null && postings.length > 0 && (
        <TableScroll label={t('availability.title')}>
          <table className="table">
            <caption className="visually-hidden">{t('availability.title')}</caption>
            <thead>
              <tr>
                {columns.map((column) => (
                  <SortableHeader key={column.key} view={view} column={column.key}>
                    {t(BOARD_HEADINGS[column.key] ?? column.key)}
                  </SortableHeader>
                ))}
              </tr>
              <tr className="table__filters">
                {columns.map((column) => (
                  <FilterCell key={column.key} view={view} column={column} label={t(BOARD_HEADINGS[column.key] ?? column.key)} />
                ))}
              </tr>
            </thead>
            <tbody>
              {view.rows.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="placeholder">
                    {t('availability.filters.noneMatch')}
                  </td>
                </tr>
              )}
              {view.rows.map((posting) => (
                <tr key={posting.id} className={historyRow(posting.standing !== 'Open')}>
                  <td>
                    <Link to={`/availability/${posting.id}`}>
                      {unitName(posting, t)}
                    </Link>
                    <br />
                    {/* The reference brokers' replies quote (2.4), beside the carrier it is for. */}
                    <span className="muted">{`${posting.reference} · ${posting.carrierName}`}</span>
                  </td>
                  <td>
                    {unitEquipment(posting, t)} · {t(`availability.loadTypes.${posting.loadType}`)}
                  </td>
                  <td className="table__numeric">
                    {posting.availablePallets !== null && (
                      <>
                        {t('availability.pallets', {
                          count: posting.availablePallets,
                          formatted: formatNumber(posting.availablePallets, i18n.language),
                        })}
                        <br />
                      </>
                    )}
                    {t('availability.pounds', {
                      formatted: formatNumber(posting.availableWeightPounds, i18n.language),
                    })}
                  </td>
                  <td>
                    {t('availability.fromUntil', {
                      from: formatDate(posting.availableFrom, i18n.language),
                      until: formatDate(posting.availableUntil, i18n.language),
                    })}
                  </td>
                  <td>{`${posting.city}, ${posting.state}`}</td>
                  <td>
                    <StandingBadge standing={posting.standing} />
                    {/* Only an open posting is on offer, so only there is a unit held
                        back worth saying: the broadcast will refuse it (2.4). */}
                    {posting.standing === 'Open' && !posting.dispatchable && (
                      <>
                        <br />
                        <span className="muted">{t('availability.heldBack')}</span>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
      {postings !== null && postings.length > 0 && <FilterSummary view={view} />}
    </section>
  )
}

/** No board yet: one array, so the view is not recomputed for an empty one every render. */
const NO_POSTINGS: TruckAvailability[] = []
